import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import HttpService from '../../../hooks/Https-services';
import PAGE_ROUTES, {
  API_ROUTES,
  permissions,
  ROLE_IDS,
  UserPermissions,
} from '../../../utils/constant';
import { GlobalSearchContext } from '../../../context/GlobalSearchContext';
import {
  Autocomplete,
  Backdrop,
  Button,
  Chip,
  CircularProgress,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useCommonDetails from '../../../hooks/useCommonDetails';

interface UserInfo {
  shiftId: string | null;
  roleId: number;
  teamId: number;
  name: string;
  email: string;
  password: string;
  middleName?: any;
  teamLeadId?: any;
}
export default function AddUser() {
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const details = useCommonDetails();
  const { _searchQuery: sQuery }: any = React.useContext(GlobalSearchContext);
  const { handleSearchChange, debouncedQuery }: any =
    useContext(GlobalSearchContext);
  const [selectedParentUser, setSelectedParentUser] = useState<any[]>();
  const [teamLeadId, setTeamLeadId] = useState('');

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    roleId: Yup.number().required('Role is required'),
    teamId: Yup.number().required('Team is required'),
    password: Yup.string().required('Password is required'),
    middleName: Yup.string(),
    // .when([], {
    //   is: () =>
    //     UserPermissions[details.isDesabled]?.includes(
    //       permissions.MIDDELE,
    //       permissions.PARENTUSER
    //     ),
    //   then: (schema) => schema.required('Middle Name is required'),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  });

  const fetchTeamsList = React.useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      setTeams(response.data.data);
    } catch (error: any) {
      console.error('Error fetching games list:', error);
    }
  }, []);

  React.useEffect(() => {
    fetchTeamsList();
  }, [fetchTeamsList]);

  // Fetch shifts list based on selected role and team
  const fetchShiftsList = useCallback(async (roleId: any, teamId: any) => {
    const query: any = { roleId, teamId };
    try {
      setLoading(true);
      const response = await HttpService.get(
        API_ROUTES.fatchShift + `?${new URLSearchParams(query).toString()}`
      );
      setShifts(response.data.data.list || []);
    } catch (error) {
      console.error('Error fetching shifts list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const onClose = () => {
    navigate(PAGE_ROUTES.USERS);
  };

  const fetchParentUser = React.useCallback(async () => {
    // Simulate API call
    try {
      const resTagger: any = await HttpService.get(
        `${API_ROUTES.leadUser}?&search=${sQuery}`
      );

      setOptions(resTagger.data.data);
    } catch (error: any) {
      console.error('Error fetching Tagger info:', error);
    }
  }, [sQuery]);
  useEffect(() => {
    fetchParentUser();
  }, [fetchParentUser]);

  // Handle changes
  const handleChange = (event: any, newValue: any) => {
    setSelectedParentUser(newValue);
    setTeamLeadId(newValue?.id);
  };
  return (
    <Box sx={{ margin: 2 }}>
      <Formik
        initialValues={{
          shiftId: null,
          roleId: 3,
          teamId: 1,
          email: '',
          name: '',
          password: '',
          middleName: '',
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const { shiftId, roleId, teamId, name, email, password, middleName } =
            values;
          const info: UserInfo = {
            shiftId,
            roleId,
            teamId,
            name,
            email,
            password,
          };

          if (
            UserPermissions[details.isDesabled]?.includes(
              // permissions.MIDDELE,
              permissions.PARENTUSER
            ) ||
            UserPermissions[details.isDesabled]?.includes(
              permissions.MIDDELE
              // permissions.PARENTUSER
            )
          ) {
            info.middleName = middleName || null;
            info.teamLeadId = teamLeadId || null;
          }
          try {
            await HttpService.post(API_ROUTES.registerUser, info);
            toast.success('Register Successfully', {
              position: 'bottom-right',
            });
            navigate(PAGE_ROUTES.USERS);
          } catch (error) {
            console.error(error);
            navigate(PAGE_ROUTES.USERS);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => {
          // Watch for changes in roleId and teamId to fetch shifts
          useEffect(() => {
            if (values.roleId && values.teamId) {
              fetchShiftsList(values.roleId, values.teamId);
              // setFieldValue("shiftId",null)
            }
          }, [values.roleId, values.teamId, fetchShiftsList]);
          return (
            <Form>
              <Card sx={{ p: 3 }}>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                  sx={{ marginTop: '10px' }}
                >
                  <Field
                    name="name"
                    as={TextField}
                    label="Name"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    helperText={
                      touched.name && errors.name ? errors.name : null
                    }
                    error={touched.name && !!errors.name}
                  />

                  <Field
                    name="email"
                    as={TextField}
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    helperText={
                      touched.email && errors.email ? errors.email : null
                    }
                    error={touched.email && !!errors.email}
                  />

                  {/* Role Dropdown */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="role-select-label">Role</InputLabel>
                    <Field
                      name="roleId"
                      as={Select}
                      labelId="role-select-label"
                      label="Role"
                      helperText={
                        touched.roleId && errors.roleId ? errors.roleId : null
                      }
                      error={touched.roleId && !!errors.roleId}
                    >
                      {Object.entries(ROLE_IDS).map(([key, id]) => (
                        <MenuItem key={id} value={id}>
                          {key.toLowerCase()}{' '}
                          {/* You can display the role name here */}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage name="roleId" component="div" />
                  </FormControl>

                  {/* Team Dropdown */}
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="team-select-label">Teams</InputLabel>
                    <Field
                      name="teamId"
                      as={Select}
                      labelId="team-select-label"
                      label="Teams"
                      helperText={
                        touched.teamId && errors.teamId ? errors.teamId : null
                      }
                      error={touched.teamId && !!errors.teamId}
                    >
                      {teams.map((team: any) => (
                        <MenuItem key={team.id} value={team.id}>
                          {team.userTeamKey}
                        </MenuItem>
                      ))}
                    </Field>
                    <ErrorMessage name="teamId" component="div" />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="shift-select-label">Shift</InputLabel>
                    <Field
                      name="shiftId"
                      as={Select}
                      labelId="shift-select-label"
                      label="Shift"
                    >
                      {shifts.length > 0 ? (
                        shifts.map((shift: any) => (
                          <MenuItem key={shift.id} value={shift.id}>
                            {shift.shiftName} &nbsp; &nbsp; Start Time:{' '}
                            {dayjs(shift?.startTime).format('HH:mm') || '00:00'}{' '}
                            &nbsp; &nbsp; End Time:{' '}
                            {dayjs(shift?.endTime).format('HH:mm') ||
                              '00:00:00'}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          No Shift Found For This Role or Team
                        </MenuItem>
                      )}
                    </Field>
                  </FormControl>

                  <Field
                    name="password"
                    as={TextField}
                    label="Password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    helperText={
                      touched.password && errors.password
                        ? errors.password
                        : null
                    }
                    error={touched.password && !!errors.password}
                  />

                  {(UserPermissions[details.isDesabled]?.includes(
                    permissions.PARENTUSER
                  ) ||
                    UserPermissions[details.isDesabled]?.includes(
                      permissions.MIDDELE
                    )) && (
                    <Field
                      name="middleName"
                      as={TextField}
                      label="Middle Name"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      helperText={
                        touched.middleName && errors.middleName
                          ? errors.middleName
                          : null
                      }
                      error={touched.middleName && !!errors.middleName}
                      // Only editable if 'EDIT' is in permissions
                    />
                  )}

                  {UserPermissions[details.isDesabled]?.includes(
                    // permissions.MIDDELE,
                    permissions.PARENTUSER
                  ) && (
                    <>
                      <FormControl fullWidth margin="normal">
                        <Autocomplete
                          options={options}
                          getOptionLabel={(option) => option.name || ''}
                          value={selectedParentUser}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          onChange={handleChange}
                          onClose={() => {
                            const data = {
                              target: {
                                value: '',
                              },
                            };
                            handleSearchChange(data);
                          }}
                          renderTags={(value: any[], getTagProps) =>
                            value.map((option: any, index: any) => (
                              <Chip
                                label={option.name}
                                {...getTagProps({ index })}

                                // View-only if 'VIEW' is in permissions
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              value={debouncedQuery}
                              onChange={(e) => handleSearchChange(e)}
                              label="Select Parent"
                              placeholder="Type to search"

                              // Only editable if 'EDIT' is in permissions
                            />
                          )}
                        />
                      </FormControl>
                    </>
                  )}
                </Box>
                <Stack
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginTop: 2,
                    gap: 2,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={isSubmitting || !isValid}
                  >
                    Create User
                  </LoadingButton>
                </Stack>
              </Card>
            </Form>
          );
        }}
      </Formik>

      {/* Backdrop with CircularProgress for loader */}
      <Backdrop
        sx={(theme) => ({
          color: '#fff',
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
